import classNames from "classnames";
import { NumberValue, ScaleLinear, axisBottom, format, select } from "d3";
import React, { MouseEventHandler, useEffect, useRef } from "react";

import styles from "./timeline-axis.module.scss";

export interface TimelineAxisProps {
  xOffset: number;
  scale: ScaleLinear<number, number>;
  setTime: (time: number) => void;
}

// convert milliseconds to time format (ex. 2.200 -> 02:20)
const formatMillisecondsToTime = (milliseconds: number) => format("05.2f")(milliseconds / 1000).replace(".", ":");

export const TimelineAxis = ({ xOffset, scale, setTime }: TimelineAxisProps) => {
  const axisRef = useRef<SVGGElement>(null);

  useEffect(() => {
    const axisElement = axisRef.current;

    if (axisElement) {
      const [minTime, maxTime] = scale.domain();
      // create a linear scale with input the time and output the timeline size range

      const axis = axisBottom(scale)
        // 10 ticks for each second
        .ticks(((maxTime || 0) - (minTime || 0)) / 100)
        // return the tick label only for round seconds, otherwise return blank
        .tickFormat((d: NumberValue) => {
          const milliseconds = d.valueOf();
          return milliseconds % 1000 === 0 ? formatMillisecondsToTime(milliseconds) : "";
        })
        // length of tick
        .tickSize(8);

      const timelineAxis = select<SVGGElement, null>(axisElement);
      timelineAxis
        .call(axis)
        .selectAll<SVGGElement, number>(".tick")
        // add a class "round-second" for seconds
        .attr("class", (d) => classNames("tick", { [styles.roundSecond || ""]: d % 1000 === 0 }));
    }

    // clean up timeline elements when re-rendering
    return () => {
      select(axisElement).selectAll("*").remove();
    };
  }, [scale]);

  // set the time when clicking on the timeline axis
  const handleClickTimelineAxis: MouseEventHandler<SVGRectElement> = (event) => {
    const { nativeEvent } = event;
    const { offsetX } = nativeEvent;
    setTime(scale.invert(offsetX - xOffset));
  };

  return (
    <g className={styles.timelineAxis} transform={`translate(${xOffset}, 2)`}>
      <g ref={axisRef} />
      <rect
        width={Math.max(scale.range()[1] || 0, 0)}
        height={20}
        fill="transparent"
        onClick={handleClickTimelineAxis}
      />
    </g>
  );
};
